/**
 * Formats a number or string representing a number as an integer with thousand separators.
 * Removes any trailing zeros and decimal parts.
 *
 * @param value The value to format
 * @param locale The locale to use for formatting (defaults to 'en-US')
 * @returns Formatted string or '-' if value is null/undefined/empty
 */
export const formatQuantity = (value: number | string | undefined | null, locale: string = 'en-US'): string => {
  if (value === undefined || value === null || value === '') return '-'
  const num = Number(value)
  if (isNaN(num)) return String(value)

  return num.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

/**
 * Formats a number with comma grouping while preserving every significant
 * digit — no rounding, no zero-padding, no truncation. Operates on the raw
 * string representation so that Postgres NUMERIC strings (returned verbatim
 * by Drizzle) keep full precision, and JS numbers go through `.toString()`
 * which gives a roundtrip-safe representation.
 *
 * Scientific notation and other non-decimal inputs are returned via
 * String(...) untouched, so we never silently round.
 */
export const formatDecimalAuto = (value: number | string | undefined | null): string => {
  if (value === undefined || value === null || value === '') return '-'

  let str: string
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return String(value)
    str = value.toString()
  } else {
    str = value.trim()
    if (str === '') return '-'
  }

  if (!/^[+-]?(\d+\.?\d*|\.\d+)$/.test(str)) return String(value)

  let sign = ''
  if (str.startsWith('-')) { sign = '-'; str = str.slice(1) }
  else if (str.startsWith('+')) { str = str.slice(1) }

  let [intPart, decPart = ''] = str.split('.')
  intPart = intPart.replace(/^0+(?=\d)/, '') || '0'
  decPart = decPart.replace(/0+$/, '')

  const intGrouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return sign + intGrouped + (decPart ? '.' + decPart : '')
}

